import { urlConfig } from '../config';
import { TPayLinkCardToken } from '../context/PaylinkUserContext';
import {
  IProduct, IProductShippingRate, IShippingTotalChargePayload, IStoreProfile, IStoreProfilePolicy
} from '../types/campaigns';
import { IAddressPayload, IUserContact, IUserDetailPayload, IUserDetails } from '../types/identity';
import {
  IAccurredPointParam, IAccurredPointResponse, ILoyaltyMemberParam, ILoyaltyMemberPayload,
  ILoyaltyMemberResponse
} from '../types/loyalty';
import { IApiResponse, IPaginationResponse } from '../types/model';
import {
  IBank, IBankVerificationPayload, IBankVerificationResponse, IGatewaySettings,
  ITransactionInitializationRequest, ITransactionInitializationResponse, IVerificationRequest,
  IVerificationResponse
} from '../types/payments';
import { ICustomer } from '../types/subscription';
import {
  ICardToken, ICardTokenPayload, ICheckoutSessionPayload, IEarnableRewardPayload,
  ILoyaltyEnrolmentPayload, IOpenSessionPayload, IOpenSessionResponse, IShipBubbleRatePayload,
  IShipBubbleRateResponse, IShippoRatePayload
} from '../types/walllet';
import { getAxiosInstance } from './instance';

const tenant = urlConfig.PAYCURRA_ADMIN_CUSTOMER_URN;

export const getStoreProfile = async (data: {
  CustomerUniqueReferenceNumber?: string;
  Slug?: string;
}): Promise<IPaginationResponse<IStoreProfile[]>> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `Campaigns/${tenant}/v1/StoreProfile`,
      method: "GET",
      params: { ...data, Page: 1, PageSize: 1, SortOrder: "NameDesc" },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCustomerDetails = async (
  customerUniqueReferenceNumber: string,
): Promise<ICustomer> => {
  try {
    const axiosInstance = getAxiosInstance();
    const response = await axiosInstance({
      url: `Subscriptions/${tenant}/v1/Customers/${customerUniqueReferenceNumber}`,
      method: "GET",
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getStoreProfilePolicy = async (data: {
  customerUniqueReferenceNumber: string;
  merchantUniqueReferenceNumber: string;
}): Promise<IApiResponse<IStoreProfilePolicy[]>> => {
  try {
    const axiosInstance = getAxiosInstance();

    const params = new URLSearchParams({
      MerchantUniqueReferenceNumber: data.merchantUniqueReferenceNumber,
    });

    const response = await axiosInstance({
      url: `Campaigns/${data.customerUniqueReferenceNumber}/v1/StoreProfilePolicy?${params}`,
      method: "GET",
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProducts = async (data: {
  CustomerUniqueReferenceNumber: string | undefined;
  VariantUniqueReferenceNumber: string | undefined;
}): Promise<IPaginationResponse<IProduct[]>> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `Campaigns/${tenant}/v1/ProductCatalogueItems`,
      method: "GET",
      params: { ...data, State: "Live" },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUserDetails = async (
  data: IUserDetailPayload,
): Promise<any> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `Identities/${urlConfig.PAYCURRA_CONSUMER_CUSTOMER_URN}/v1/Users/${data.userUniqueReferenceNumber}/details`,
      method: "PATCH",
      data,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getUserDetails = async (
  userUniqueReferenceNumber: string,
): Promise<IUserDetails> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `Identities/${urlConfig.PAYCURRA_CONSUMER_CUSTOMER_URN}/v1/Users/${userUniqueReferenceNumber}`,
      method: "GET",
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addShippingAddress = async (
  data: IAddressPayload,
): Promise<IUserContact[]> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `Identities/${urlConfig.PAYCURRA_CONSUMER_CUSTOMER_URN}/v1/Users/${data.userUniqueReferenceNumber}/contact/${data.uniqueReferenceNumber}`,
      method: "PATCH",
      data,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getShippingAddress = async (data: {
  userUniqueReferenceNumber: string;
}): Promise<IUserContact[]> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `Identities/${urlConfig.PAYCURRA_CONSUMER_CUSTOMER_URN}/v1/Users/${data.userUniqueReferenceNumber}/contact`,
      method: "GET",
      params: data,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteShippingAddress = async (data: {
  userUniqueReferenceNumber: string;
  addressUniqueReferenceNumber: string;
}): Promise<any> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `Identities/${urlConfig.PAYCURRA_CONSUMER_CUSTOMER_URN}/v1/Users/${data.userUniqueReferenceNumber}/contact/${data.addressUniqueReferenceNumber}`,
      method: "DELETE",
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getShippingTotalCharge = async (
  data: IShippingTotalChargePayload,
): Promise<IApiResponse<number>> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `Campaigns/${data?.merchantUniqueReferenceNumber}/v1/Shipping/${data?.merchantUniqueReferenceNumber}/totalShippingCharges`,
      method: "POST",
      data,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllShippingRates = async (data: {
  customerUniqueReferenceNumber: string;
  merchantUniqueReferenceNumber: string;
  productCatalogueItemUniqueReferenceNumber: string;
  page: string;
  pageSize: string;
}): Promise<IPaginationResponse<IProductShippingRate[]>> => {
  try {
    const axiosInstance = getAxiosInstance();

    const params = new URLSearchParams({
      MerchantUniqueReferenceNumber: data.merchantUniqueReferenceNumber,
      ProductCatalogueUniqueReferenceNumber:
        data.productCatalogueItemUniqueReferenceNumber,
      page: data.page,
      pageSize: data.pageSize,
    });

    const response = await axiosInstance({
      url: `Campaigns/${data.customerUniqueReferenceNumber}/v1/Shipping/${data.merchantUniqueReferenceNumber}/rates?${params}`,
      method: "GET",
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getShippingRateFromShipBubble = async (
  data: IShipBubbleRatePayload,
): Promise<IShipBubbleRateResponse> => {
  const {
    customerUniqueReferenceNumber,
    sessionUniqueReferenceNumber,
    ...requestBody
  } = data;
  try {
    const axiosInstance = getAxiosInstance();
    const response = await axiosInstance({
      url: `Wallets/${customerUniqueReferenceNumber}/V1/Shipments/${customerUniqueReferenceNumber}/cart/${sessionUniqueReferenceNumber}/rates`,
      method: "POST",
      data: requestBody,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getShippingRateFromShippo = async (
  data: IShippoRatePayload,
): Promise<IShipBubbleRateResponse> => {
  // customerUniqueReferenceNumber and sessionUniqueReferenceNumber are path params only.
  // Only the PostShippoBestRatesRequest fields go in the body.
  const {
    customerUniqueReferenceNumber,
    sessionUniqueReferenceNumber,
    ...requestBody
  } = data;
  try {
    const axiosInstance = getAxiosInstance();
    const response = await axiosInstance({
      url: `Wallets/${customerUniqueReferenceNumber}/V1/Shipments/${customerUniqueReferenceNumber}/cart/${sessionUniqueReferenceNumber}/rates/shippo`,
      method: 'POST',
      data: requestBody,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};


export const openWalletSession = async (
  data: IOpenSessionPayload,
): Promise<IApiResponse<IOpenSessionResponse>> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `Wallets/${data.consumerUniqueReferenceNumber}/v1/Sessions`,
      method: "POST",
      data,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCardToken = async ({
  customerUniqueReferenceNumber,
  merchantUniqueReferenceNumber,
  walletUniqueReferenceNumber,
  consumerUniqueReferenceNumber,
}: {
  customerUniqueReferenceNumber: string | undefined;
  merchantUniqueReferenceNumber: string | undefined;
  walletUniqueReferenceNumber: string | undefined;
  consumerUniqueReferenceNumber: string | undefined;
}): Promise<IApiResponse<ICardToken[]>> => {
  try {
    const axiosInstance = getAxiosInstance();

    const res = await axiosInstance({
      url: `/wallets/${customerUniqueReferenceNumber}/v1/CardTokens?customerUniqueReferenceNumber=${customerUniqueReferenceNumber}&merchantUniqueReferenceNumber=${merchantUniqueReferenceNumber}&consumerUniqueReferenceNumber=${consumerUniqueReferenceNumber}&walletUniqueReferenceNumber=${walletUniqueReferenceNumber}`,
      method: "GET",
    });

    return res.data;
  } catch (error) {
    throw error;
  }
};

export const checkoutWalletSession = async (
  data: ICheckoutSessionPayload,
): Promise<IApiResponse<any>> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `Wallets/${data.merchantUniqueReferenceNumber}/v1/Sessions/${data.sessionUniqueReferenceNumber}/checkouts`,
      method: "POST",
      data,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getGatewaySettings = async ({
  customerUniqueReferenceNumber,
}: {
  customerUniqueReferenceNumber: string;
}): Promise<IGatewaySettings[]> => {
  try {
    const axiosInstance = getAxiosInstance();
    const response = await axiosInstance({
      url: `payments/${customerUniqueReferenceNumber}/v1/GatewaySettings`,
      method: "GET",
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getBanks = async ({
  gateway,
  country,
  customerUniqueReferenceNumber,
}: {
  gateway: string;
  country: string;
  customerUniqueReferenceNumber: string;
}): Promise<IApiResponse<IBank[]>> => {
  try {
    const axiosInstance = getAxiosInstance();
    const response = await axiosInstance({
      url: `payments/${customerUniqueReferenceNumber}/v1/Methods/${gateway}/banks/${country}?pageSize=50`,
      method: "GET",
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const verifyBankAccountNumber = async (
  data: IBankVerificationPayload,
): Promise<IApiResponse<IBankVerificationResponse>> => {
  try {
    const axiosInstance = getAxiosInstance();
    const response = await axiosInstance({
      url: `payments/${data.customerUniqueReferenceNumber}/v1/Methods/${data.gateway}/banks`,
      method: "POST",
      data,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const initPayLinkTransaction = async (
  data: ITransactionInitializationRequest,
): Promise<ITransactionInitializationResponse> => {
  try {
    const axiosInstance = getAxiosInstance();
    const response = await axiosInstance({
      url: `payments/${data.customerUniqueReferenceNumber}/v1/Transactions`,
      method: "POST",
      data,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const verifyPayLinkTransaction = async (
  data: IVerificationRequest,
): Promise<IVerificationResponse> => {
  const { uniqueReferenceNumber, customerUniqueReferenceNumber, ...rest } =
    data;
  try {
    const axiosInstance = getAxiosInstance();
    const response = await axiosInstance({
      url: `payments/${customerUniqueReferenceNumber}/v1/Transactions/${uniqueReferenceNumber}/verifications`,
      method: "POST",
      data: { customerUniqueReferenceNumber, ...rest },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createPayLinkCardToken = async (
  payload: ICardTokenPayload,
): Promise<IApiResponse<ICardToken>> => {
  const { customerUniqueReferenceNumber, ...rest } = payload;
  try {
    const axiosInstance = getAxiosInstance();
    const response = await axiosInstance({
      url: `wallets/${customerUniqueReferenceNumber}/v1/CardTokens`,
      method: "POST",
      data: { customerUniqueReferenceNumber, ...rest },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removePayLinkCardToken = async ({
  uniqueReferenceNumber,
  customerUniqueReferenceNumber,
  walletUniqueReferenceNumber,
  gatewayCustomerId,
  emailAddress,
  paymentMethodUniqueReferenceNumber,
}: {
  uniqueReferenceNumber: string;
  customerUniqueReferenceNumber: string;
  walletUniqueReferenceNumber: string;
  gatewayCustomerId: string;
  emailAddress: string;
  paymentMethodUniqueReferenceNumber: string;
}): Promise<IApiResponse<any>> => {
  try {
    const axiosInstance = getAxiosInstance();
    const params = new URLSearchParams({
      WalletUniqueReferenceNumber: walletUniqueReferenceNumber,
      GatewayCustomerId: gatewayCustomerId,
      CustomerEmailAddress: emailAddress,
      PaymentMethodUniqueReferenceNumber: paymentMethodUniqueReferenceNumber,
    });

    const response = await axiosInstance({
      url: `wallets/${customerUniqueReferenceNumber}/v1/CardTokens/${uniqueReferenceNumber}?${params}`,
      method: "DELETE",
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAdminDetails = async (data: {
  accountType: "Admin";
}): Promise<IPaginationResponse<ICustomer[]>> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `Subscriptions/${tenant}/v1/Customers`,
      method: "GET",
      params: { ...data, PageSize: 1, Page: 1 },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getEarnableRewards = async (
  data: IEarnableRewardPayload,
): Promise<IApiResponse<any>> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `Wallets/${data.customerUniqueReferenceNumber}/v1/Sessions/${data.uniqueReferenceNumber}/rewards`,
      method: "POST",
      data,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllEnrolledLoyalty = async (
  data: ILoyaltyMemberParam,
): Promise<IApiResponse<ILoyaltyMemberResponse[]>> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `/Loyalties/${data.CustomerUniqueReferenceNumber}/v1/Members`,
      method: "GET",
      params: data,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const enrollForLoyalty = async (data: ILoyaltyEnrolmentPayload) => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `/Wallets/${data.customerUniqueReferenceNumber}/v1/Points`,
      method: "POST",
      data,
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const getAccuredPoint = async (
  data: IAccurredPointParam,
): Promise<IPaginationResponse<IAccurredPointResponse[]>> => {
  try {
    const axiosInstance = getAxiosInstance();

    const response = await axiosInstance({
      url: `/Loyalties/${data.CustomerUniqueReferenceNumber}/v1/AccrualPointAggregation`,
      method: "GET",
      params: {
        ...data,
        GroupByClause: "LoyaltyProgrammeUniqueReferenceNumber",
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};
